import React, { useState, useEffect, useRef } from 'react';
import { X, MessageCircle, Gift, Sparkles, ThumbsUp, ChevronRight, Star, Trophy, Heart, MapPin, Clock, Utensils } from 'lucide-react';
import { Button } from './ui/button';

// Restaurant Knowledge Base - Mr. Frogs Info
const RESTAURANT_INFO = {
  name: "Mr. Frogs",
  slogan: "¡El mejor sabor del mar!",
  specialties: [
    "Mariscos frescos",
    "Cocteles de camarón",
    "Ceviches",
    "Tacos de marlín",
    "Clamato preparado"
  ],
  popularDishes: [
    { name: "Taco de Marlín", description: "Nuestro clásico más pedido" },
    { name: "Clamato Preparado", description: "Refrescante y picosito" },
    { name: "Ceviche de Camarón", description: "Fresco del día" },
    { name: "Tostada de Ceviche", description: "Crujiente y deliciosa" }
  ],
  funFacts: [
    "🐸 Nuestro nombre viene de la rana más cool del océano",
    "🌊 Todos nuestros mariscos son frescos del día",
    "🏆 Somos el favorito de la familia desde hace años",
    "💚 El verde es nuestro color porque representa frescura"
  ],
  vipBenefits: {
    Bronce: "5% de descuento en todas tus compras",
    Plata: "5% descuento + puntos 1.5x más rápido",
    Oro: "10% descuento + puntos 2x + sorpresas de cumpleaños",
    Diamante: "15% descuento + puntos 3x + acceso VIP exclusivo"
  }
};

// Smart Frog Character Component
const FrogCharacter = ({ mood, isWalking, direction, isThinking }) => {
  return (
    <svg 
      viewBox="0 0 100 100" 
      className={`w-full h-full transition-transform duration-300 ${direction === 'left' ? 'scale-x-[-1]' : ''}`}
    >
      {/* Frog Body */}
      <ellipse cx="50" cy="60" rx="35" ry="30" fill="#2E7D32" />
      <ellipse cx="50" cy="65" rx="25" ry="20" fill="#81C784" />
      
      {/* Head */}
      <ellipse cx="50" cy="35" rx="30" ry="25" fill="#2E7D32" />
      
      {/* Eyes */}
      <circle cx="35" cy="22" r="14" fill="#2E7D32" />
      <circle cx="65" cy="22" r="14" fill="#2E7D32" />
      <circle cx="35" cy="22" r="11" fill="white" />
      <circle cx="65" cy="22" r="11" fill="white" />
      
      {/* Pupils - animated */}
      <circle cx={isThinking ? "38" : "35"} cy={isThinking ? "20" : "22"} r="6" fill="#1B5E20">
        {isWalking && <animate attributeName="cx" values="33;37;33" dur="0.5s" repeatCount="indefinite" />}
        {isThinking && <animate attributeName="cy" values="18;22;18" dur="1s" repeatCount="indefinite" />}
      </circle>
      <circle cx={isThinking ? "68" : "65"} cy={isThinking ? "20" : "22"} r="6" fill="#1B5E20">
        {isWalking && <animate attributeName="cx" values="63;67;63" dur="0.5s" repeatCount="indefinite" />}
        {isThinking && <animate attributeName="cy" values="18;22;18" dur="1s" repeatCount="indefinite" />}
      </circle>
      
      {/* Eye Shine */}
      <circle cx="32" cy="19" r="3" fill="white" opacity="0.8" />
      <circle cx="62" cy="19" r="3" fill="white" opacity="0.8" />
      
      {/* Thinking bubbles */}
      {isThinking && (
        <>
          <circle cx="80" cy="15" r="3" fill="#81C784" opacity="0.6">
            <animate attributeName="opacity" values="0.3;0.8;0.3" dur="1s" repeatCount="indefinite" />
          </circle>
          <circle cx="88" cy="8" r="4" fill="#81C784" opacity="0.6">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="1s" repeatCount="indefinite" />
          </circle>
          <circle cx="95" cy="0" r="5" fill="#81C784" opacity="0.6">
            <animate attributeName="opacity" values="0.7;1;0.7" dur="1s" repeatCount="indefinite" />
          </circle>
        </>
      )}
      
      {/* Smile */}
      <path 
        d={mood === 'happy' || mood === 'excited' ? "M 35 45 Q 50 58 65 45" : "M 38 48 Q 50 52 62 48"}
        stroke="#1B5E20" strokeWidth="3" fill="none" strokeLinecap="round"
      />
      
      {/* Heart eyes when showing love */}
      {mood === 'love' && (
        <>
          <path d="M 30 20 C 28 16, 35 16, 35 22 C 35 16, 42 16, 40 20 L 35 26 Z" fill="#E91E63" />
          <path d="M 60 20 C 58 16, 65 16, 65 22 C 65 16, 72 16, 70 20 L 65 26 Z" fill="#E91E63" />
        </>
      )}
      
      {/* Tongue */}
      {mood === 'excited' && (
        <path d="M 50 52 Q 50 65 55 68" stroke="#E91E63" strokeWidth="4" fill="none" strokeLinecap="round">
          <animate attributeName="d" values="M 50 52 Q 50 60 55 63;M 50 52 Q 50 68 55 72;M 50 52 Q 50 60 55 63" dur="0.3s" repeatCount="3" />
        </path>
      )}
      
      {/* Spots */}
      <circle cx="30" cy="55" r="5" fill="#1B5E20" opacity="0.3" />
      <circle cx="70" cy="55" r="4" fill="#1B5E20" opacity="0.3" />
      <circle cx="50" cy="75" r="6" fill="#1B5E20" opacity="0.3" />
      
      {/* Legs with walking animation */}
      <ellipse cx="25" cy="80" rx="12" ry="8" fill="#2E7D32">
        {isWalking && <animate attributeName="cy" values="80;78;80" dur="0.3s" repeatCount="indefinite" />}
      </ellipse>
      <ellipse cx="75" cy="80" rx="12" ry="8" fill="#2E7D32">
        {isWalking && <animate attributeName="cy" values="78;80;78" dur="0.3s" repeatCount="indefinite" />}
      </ellipse>
      
      {/* Cheeks */}
      {(mood === 'happy' || mood === 'excited' || mood === 'love') && (
        <>
          <circle cx="25" cy="40" r="6" fill="#E91E63" opacity="0.3" />
          <circle cx="75" cy="40" r="6" fill="#E91E63" opacity="0.3" />
        </>
      )}
      
      {/* Chef hat for restaurant mascot */}
      <ellipse cx="50" cy="8" rx="20" ry="8" fill="white" />
      <rect x="35" y="0" width="30" height="12" fill="white" />
      <ellipse cx="50" cy="0" rx="15" ry="6" fill="white" />
    </svg>
  );
};

const SmartFrogMascot = ({ 
  customerData, 
  onProductClick, 
  redeemableProducts = [],
  unreadNotifications = 0,
  redemptionHistory = []
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [currentMessage, setCurrentMessage] = useState('');
  const [frogMood, setFrogMood] = useState('idle');
  const [showChat, setShowChat] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [recommendedProduct, setRecommendedProduct] = useState(null);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [isWalking, setIsWalking] = useState(false);
  const [frogPosition, setFrogPosition] = useState({ x: 0, y: 0 });
  const [walkDirection, setWalkDirection] = useState('right');
  const [isThinking, setIsThinking] = useState(false);
  const [customerInsights, setCustomerInsights] = useState(null);

  // Analyze customer data for personalization
  useEffect(() => {
    if (customerData?.customer) {
      const customer = customerData.customer;
      // Points can be in different places depending on the data structure
      const pointsBalance = customerData.points?.balance || customer.points || 0;
      
      // Analyze order history to learn customer preferences
      const history = customerData.history || [];
      const orderDescriptions = history
        .filter(h => h.points > 0) // Only purchases (positive points)
        .map(h => h.description);
      
      // Find most frequent items/patterns
      const itemCounts = {};
      orderDescriptions.forEach(desc => {
        // Extract product names from descriptions like "Compra: Taco de Marlin x2"
        const match = desc.match(/Compra:?\s*(.+)/i) || desc.match(/Pedido:?\s*(.+)/i);
        if (match) {
          const items = match[1].split(',').map(i => i.trim());
          items.forEach(item => {
            const cleanItem = item.replace(/\s*x\d+$/, '').trim(); // Remove quantity
            if (cleanItem && cleanItem.length > 2) {
              itemCounts[cleanItem] = (itemCounts[cleanItem] || 0) + 1;
            }
          });
        }
      });
      
      // Sort by frequency to get favorites
      const favoriteItems = Object.entries(itemCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name, count]) => ({ name, count }));
      
      // Get last order description
      const lastOrder = history.find(h => h.points > 0);
      const lastOrderDesc = lastOrder ? lastOrder.description : null;
      
      const insights = {
        name: customer.name || 'Amigo',
        firstName: (customer.name || 'Amigo').split(' ')[0],
        points: pointsBalance,
        visits: customer.visits || customer.visit_count || 0,
        totalSpent: customer.total_spent || 0,
        vipLevel: customerData.tier?.label || customer.vip_level || 'Bronce',
        isNewCustomer: (customer.visits || customer.visit_count || 0) <= 2,
        isFrequent: (customer.visits || customer.visit_count || 0) >= 10,
        isHighSpender: (customer.total_spent || 0) >= 2000,
        pointsValue: (pointsBalance * 0.10).toFixed(2),
        canRedeem: redeemableProducts.some(p => pointsBalance >= p.points_cost),
        affordableProducts: redeemableProducts.filter(p => pointsBalance >= p.points_cost),
        almostAffordable: redeemableProducts.filter(p => {
          const diff = p.points_cost - pointsBalance;
          return diff > 0 && diff <= 100;
        }),
        // New: Order history insights
        orderCount: history.filter(h => h.points > 0).length,
        favoriteItems: favoriteItems,
        lastOrder: lastOrderDesc,
        hasOrderHistory: history.length > 0
      };
      setCustomerInsights(insights);
    }
  }, [customerData, redeemableProducts]);

  // Personalized greeting based on customer data
  const getPersonalizedGreeting = () => {
    if (!customerInsights) return "¡Hola! Soy Froggy 🐸";
    
    const { firstName, isNewCustomer, isFrequent, visits, vipLevel, canRedeem } = customerInsights;
    
    if (isNewCustomer) {
      return `¡Bienvenido a ${RESTAURANT_INFO.name}, ${firstName}! 🐸 Soy Froggy, tu guía VIP`;
    }
    
    if (isFrequent) {
      return `¡${firstName}! ¡Mi cliente favorito! 🐸💚 Ya van ${visits} visitas juntos`;
    }
    
    if (canRedeem) {
      return `¡Hola ${firstName}! 🎁 ¡Tienes puntos para canjear premios!`;
    }
    
    const greetings = [
      `¡${firstName}! ¡Qué gusto verte! 🐸`,
      `¡Hola ${firstName}! ¿Listo para algo delicioso?`,
      `¡${firstName} está aquí! 🎉 ¡Croac!`,
    ];
    
    return greetings[Math.floor(Math.random() * greetings.length)];
  };

  // Initial greeting
  useEffect(() => {
    if (customerData && customerInsights && !hasGreeted) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsMinimized(false);
        setCurrentMessage(getPersonalizedGreeting());
        setFrogMood(customerInsights.isFrequent ? 'love' : 'excited');
        setHasGreeted(true);
        playFrogSound('greeting');
        doHopAnimation();
        
        setTimeout(() => {
          setIsMinimized(true);
          setFrogMood('idle');
        }, 6000);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [customerData, customerInsights, hasGreeted]);

  // Smart offers based on customer behavior
  useEffect(() => {
    if (!customerData || !isVisible || !customerInsights) return;
    
    const offerInterval = setInterval(() => {
      if (isMinimized && !showOffer && Math.random() > 0.5) {
        showSmartOffer();
      }
    }, 40000);
    
    return () => clearInterval(offerInterval);
  }, [customerData, isVisible, isMinimized, showOffer, customerInsights]);

  // Random walking
  useEffect(() => {
    if (!customerData || !isVisible || !isMinimized) return;
    
    const walkInterval = setInterval(() => {
      if (Math.random() > 0.7) {
        startWalking();
      }
    }, 25000);
    
    return () => clearInterval(walkInterval);
  }, [customerData, isVisible, isMinimized]);

  const doHopAnimation = () => {
    let hopCount = 0;
    const hopInterval = setInterval(() => {
      setFrogPosition(prev => ({ ...prev, y: hopCount % 2 === 0 ? -15 : 0 }));
      hopCount++;
      if (hopCount > 5) {
        clearInterval(hopInterval);
        setFrogPosition({ x: 0, y: 0 });
      }
    }, 150);
  };

  const startWalking = () => {
    setIsWalking(true);
    setFrogMood('happy');
    
    const direction = Math.random() > 0.5 ? 'left' : 'right';
    setWalkDirection(direction);
    
    const maxDistance = 80;
    const targetX = direction === 'left' ? -maxDistance : maxDistance;
    let currentX = 0;
    const walkStep = direction === 'left' ? -4 : 4;
    
    const walkAnimation = setInterval(() => {
      currentX += walkStep;
      setFrogPosition(prev => ({ ...prev, x: currentX }));
      
      if (Math.abs(currentX) >= Math.abs(targetX)) {
        clearInterval(walkAnimation);
        setTimeout(() => {
          const returnAnimation = setInterval(() => {
            currentX -= walkStep;
            setFrogPosition(prev => ({ ...prev, x: currentX }));
            if (currentX === 0) {
              clearInterval(returnAnimation);
              setIsWalking(false);
              setFrogMood('idle');
              setWalkDirection('right');
            }
          }, 50);
        }, 500);
      }
    }, 50);
  };

  const playFrogSound = (type) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (type === 'greeting') {
        oscillator.frequency.setValueAtTime(150, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(300, audioContext.currentTime + 0.1);
        oscillator.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.2);
        oscillator.frequency.exponentialRampToValueAtTime(250, audioContext.currentTime + 0.3);
      } else if (type === 'celebrate') {
        oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.15);
        oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.3);
      } else {
        oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(280, audioContext.currentTime + 0.15);
      }
      
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.12, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } catch (error) {}
  };

  const showSmartOffer = () => {
    if (!customerInsights) return;
    
    setIsMinimized(false);
    setShowOffer(true);
    setFrogMood('excited');
    doHopAnimation();
    
    // Smart recommendation based on customer
    if (customerInsights.almostAffordable.length > 0) {
      const product = customerInsights.almostAffordable[0];
      const pointsNeeded = product.points_cost - customerInsights.points;
      setCurrentMessage(`¡${customerInsights.firstName}! Te faltan solo ${pointsNeeded} puntos para ${product.name} 🎯`);
      setRecommendedProduct(product);
    } else if (customerInsights.affordableProducts.length > 0) {
      const product = customerInsights.affordableProducts[Math.floor(Math.random() * customerInsights.affordableProducts.length)];
      setCurrentMessage(`¡${customerInsights.firstName}! ¡Puedes canjear ${product.name} ahora! 🎁`);
      setRecommendedProduct(product);
    } else if (redeemableProducts.length > 0) {
      const product = redeemableProducts[Math.floor(Math.random() * Math.min(5, redeemableProducts.length))];
      setCurrentMessage(`¿Ya probaste ${product.name}? ¡Es de los favoritos! 😋`);
      setRecommendedProduct(product);
    } else {
      const fact = RESTAURANT_INFO.funFacts[Math.floor(Math.random() * RESTAURANT_INFO.funFacts.length)];
      setCurrentMessage(fact);
    }
    
    playFrogSound('chirp');
    
    setTimeout(() => {
      setShowOffer(false);
      setIsMinimized(true);
      setFrogMood('idle');
      setRecommendedProduct(null);
    }, 8000);
  };

  const handleChatOpen = () => {
    setShowChat(true);
    setIsMinimized(false);
    setFrogMood('happy');
    
    if (chatHistory.length === 0 && customerInsights) {
      setChatHistory([
        { from: 'frog', message: `¡Hola ${customerInsights.firstName}! 🐸 Soy Froggy de ${RESTAURANT_INFO.name}. ¿En qué te ayudo?` }
      ]);
    }
  };

  const handleChatOption = (option) => {
    if (!customerInsights) return;
    
    setIsThinking(true);
    
    setTimeout(() => {
      setIsThinking(false);
      let response = '';
      let userMessage = '';
      
      switch(option) {
        case 'recommend':
          userMessage = '¿Qué me recomiendas?';
          setFrogMood('excited');
          doHopAnimation();
          
          // Smart recommendation based on order history
          if (customerInsights.favoriteItems && customerInsights.favoriteItems.length > 0) {
            const favorite = customerInsights.favoriteItems[0];
            if (customerInsights.affordableProducts.length > 0) {
              const product = customerInsights.affordableProducts[0];
              response = `¡${customerInsights.firstName}! 🧠 Sé que te gusta "${favorite.name}" (lo has pedido ${favorite.count} veces).\n\nCon tus ${customerInsights.points.toLocaleString()} puntos puedes canjear ${product.name}. ¡Te lo recomiendo! 🎁`;
              setRecommendedProduct(product);
            } else {
              response = `¡${customerInsights.firstName}! 🧠 Veo que tu favorito es "${favorite.name}" - ¡tienes buen gusto!\n\nSigue acumulando puntos para canjear premios gratis 🎁`;
            }
          } else if (customerInsights.affordableProducts.length > 0) {
            const product = customerInsights.affordableProducts[0];
            response = `¡${customerInsights.firstName}! Con tus ${customerInsights.points.toLocaleString()} puntos te recomiendo ${product.name} (${product.points_cost} pts). ¡Lo puedes canjear ahora! 🎁`;
            setRecommendedProduct(product);
          } else if (redeemableProducts.length > 0) {
            const popular = RESTAURANT_INFO.popularDishes[Math.floor(Math.random() * RESTAURANT_INFO.popularDishes.length)];
            response = `Te recomiendo nuestro ${popular.name} - ${popular.description}. ¡Es de los más pedidos! 🌟`;
          } else {
            response = `¡Prueba nuestras especialidades: ${RESTAURANT_INFO.specialties.slice(0, 3).join(', ')}! Son increíbles 😋`;
          }
          break;
          
        case 'favorites':
          userMessage = '¿Cuáles son mis favoritos?';
          setFrogMood('happy');
          
          if (customerInsights.favoriteItems && customerInsights.favoriteItems.length > 0) {
            const favList = customerInsights.favoriteItems
              .map((f, i) => `${i + 1}. ${f.name} (${f.count}x)`)
              .join('\n');
            response = `🧠 ¡Te conozco bien ${customerInsights.firstName}!\n\nTus favoritos son:\n${favList}\n\n¡Tienes excelente gusto! 😋`;
          } else {
            response = `${customerInsights.firstName}, aún no tengo suficiente historial tuyo. ¡Sigue visitándonos para conocer tus gustos! 🐸`;
          }
          break;
          
        case 'points':
          userMessage = '¿Cómo van mis puntos?';
          setFrogMood('happy');
          
          const pointsInfo = `¡${customerInsights.firstName}, tienes ${customerInsights.points.toLocaleString()} puntos! 💰\n\nEso equivale a $${customerInsights.pointsValue} MXN`;
          
          if (customerInsights.almostAffordable.length > 0) {
            const almost = customerInsights.almostAffordable[0];
            const needed = almost.points_cost - customerInsights.points;
            response = `${pointsInfo}\n\n¡Te faltan solo ${needed} puntos para ${almost.name}! 🎯`;
          } else if (customerInsights.canRedeem) {
            response = `${pointsInfo}\n\n¡Ya puedes canjear premios! Toca "Canjear" abajo 🎁`;
          } else {
            response = `${pointsInfo}\n\n¡Sigue acumulando con cada compra!`;
          }
          break;
          
        case 'level':
          userMessage = '¿Cuál es mi nivel VIP?';
          setFrogMood('love');
          
          const levelBenefit = RESTAURANT_INFO.vipBenefits[customerInsights.vipLevel] || "Beneficios especiales";
          response = `¡Eres nivel ${customerInsights.vipLevel}! 🏆\n\nBeneficios: ${levelBenefit}\n\nLlevas ${customerInsights.visits} visitas y $${customerInsights.totalSpent.toLocaleString()} gastados. ¡Gracias por tu preferencia! 💚`;
          break;
          
        case 'restaurant':
          userMessage = 'Cuéntame de Mr. Frogs';
          setFrogMood('excited');
          doHopAnimation();
          
          const fact = RESTAURANT_INFO.funFacts[Math.floor(Math.random() * RESTAURANT_INFO.funFacts.length)];
          response = `¡${RESTAURANT_INFO.name} - ${RESTAURANT_INFO.slogan}! 🐸\n\nNuestras especialidades:\n• ${RESTAURANT_INFO.specialties.join('\n• ')}\n\n${fact}`;
          break;
          
        case 'help':
          userMessage = '¿Cómo funciona el programa?';
          response = `¡Es muy fácil ${customerInsights.firstName}! 🎓\n\n1️⃣ Compra → Ganas puntos\n2️⃣ Acumula → Sube de nivel VIP\n3️⃣ Canjea → Premios gratis\n\n💡 1 punto = $0.10 MXN\n🎁 Canjea cuando quieras en "Canjear"`;
          break;
          
        case 'bye':
          userMessage = '¡Adiós Froggy!';
          setFrogMood('love');
          response = `¡Hasta pronto ${customerInsights.firstName}! 🐸💚\n\n¡Gracias por visitar ${RESTAURANT_INFO.name}! Te esperamos pronto. ¡Croac!`;
          setTimeout(() => {
            setShowChat(false);
            setIsMinimized(true);
            setFrogMood('idle');
          }, 2500);
          break;
          
        default:
          response = '¡Croac! 🐸';
      }
      
      setChatHistory(prev => [
        ...prev,
        { from: 'user', message: userMessage },
        { from: 'frog', message: response }
      ]);
      
      playFrogSound('chirp');
    }, 800);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Minimized Frog */}
      {isMinimized && (
        <div
          className="fixed bottom-36 right-4 z-[9999] transition-all duration-100"
          style={{ transform: `translate(${frogPosition.x}px, ${frogPosition.y}px)` }}
        >
          <button
            onClick={() => { setIsMinimized(false); handleChatOpen(); }}
            className={`w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-xl flex items-center justify-center hover:scale-110 transition-transform border-3 border-white overflow-hidden ${isWalking ? 'animate-bounce' : ''}`}
            data-testid="frog-mascot-button"
          >
            <FrogCharacter mood={frogMood} isWalking={isWalking} direction={walkDirection} isThinking={false} />
          </button>
          {unreadNotifications > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold animate-pulse">
              {unreadNotifications}
            </span>
          )}
          {isWalking && (
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white px-2 py-1 rounded-full text-xs shadow-md whitespace-nowrap">
              🔍 Buscando ofertas...
            </div>
          )}
        </div>
      )}

      {/* Expanded Interface */}
      {!isMinimized && (
        <div className="fixed bottom-36 right-4 z-[9999] w-72 max-w-[calc(100vw-2rem)]" style={{ transform: `translateY(${frogPosition.y}px)` }}>
          
          {/* Speech Bubble */}
          {!showChat && currentMessage && (
            <div className="absolute bottom-full right-0 mb-2 bg-white rounded-2xl rounded-br-none p-4 shadow-xl border-2 border-green-400 max-w-full animate-fadeIn">
              <button onClick={() => setIsMinimized(true)} className="absolute -top-2 -right-2 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300">
                <X className="w-4 h-4" />
              </button>
              <p className="text-sm text-gray-700 pr-4 font-medium whitespace-pre-line">{currentMessage}</p>
              
              {recommendedProduct && (
                <div 
                  onClick={() => onProductClick && onProductClick(recommendedProduct)}
                  className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 cursor-pointer hover:shadow-md transition-all hover:scale-[1.02]"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">🎁</div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-800">{recommendedProduct.name}</p>
                      <p className="text-xs text-green-600 font-medium">{recommendedProduct.points_cost} puntos</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              )}
              
              {!showOffer && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={handleChatOpen}>
                    <MessageCircle className="w-3 h-3 mr-1" /> Chatear
                  </Button>
                  <Button size="sm" className="flex-1 text-xs bg-green-500 hover:bg-green-600" onClick={() => setIsMinimized(true)}>
                    <ThumbsUp className="w-3 h-3 mr-1" /> ¡Gracias!
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Chat Interface */}
          {showChat && (
            <div className="bg-white rounded-2xl shadow-xl border-2 border-green-400 overflow-hidden animate-fadeIn">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                    <FrogCharacter mood={frogMood} isWalking={false} direction="right" isThinking={isThinking} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Froggy</p>
                    <p className="text-green-100 text-xs">{isThinking ? 'Pensando...' : `Asistente ${RESTAURANT_INFO.name}`}</p>
                  </div>
                </div>
                <button onClick={() => { setShowChat(false); setIsMinimized(true); }} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="h-52 overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-green-50 to-white">
                {chatHistory.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.from === 'frog' && (
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2 flex-shrink-0">
                        🐸
                      </div>
                    )}
                    <div className={`max-w-[75%] p-3 rounded-2xl text-sm whitespace-pre-line ${
                      msg.from === 'user' ? 'bg-green-500 text-white rounded-br-sm' : 'bg-white border border-green-200 rounded-bl-sm shadow-sm'
                    }`}>
                      {msg.message}
                    </div>
                  </div>
                ))}
                {isThinking && (
                  <div className="flex justify-start">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">🐸</div>
                    <div className="bg-white border border-green-200 rounded-2xl rounded-bl-sm p-3 shadow-sm">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-3 border-t bg-white grid grid-cols-3 gap-2">
                <button onClick={() => handleChatOption('recommend')} className="p-2.5 bg-orange-50 hover:bg-orange-100 rounded-xl text-xs text-orange-700 flex items-center justify-center gap-1 transition-all hover:scale-[1.02] font-medium">
                  <Gift className="w-4 h-4" /> Recomiéndame
                </button>
                <button onClick={() => handleChatOption('favorites')} className="p-2.5 bg-pink-50 hover:bg-pink-100 rounded-xl text-xs text-pink-700 flex items-center justify-center gap-1 transition-all hover:scale-[1.02] font-medium">
                  <Heart className="w-4 h-4" /> Mis favoritos
                </button>
                <button onClick={() => handleChatOption('points')} className="p-2.5 bg-green-50 hover:bg-green-100 rounded-xl text-xs text-green-700 flex items-center justify-center gap-1 transition-all hover:scale-[1.02] font-medium">
                  <Sparkles className="w-4 h-4" /> Mis puntos
                </button>
                <button onClick={() => handleChatOption('level')} className="p-2.5 bg-yellow-50 hover:bg-yellow-100 rounded-xl text-xs text-yellow-700 flex items-center justify-center gap-1 transition-all hover:scale-[1.02] font-medium">
                  <Trophy className="w-4 h-4" /> Mi nivel VIP
                </button>
                <button onClick={() => handleChatOption('restaurant')} className="p-2.5 bg-blue-50 hover:bg-blue-100 rounded-xl text-xs text-blue-700 flex items-center justify-center gap-1 transition-all hover:scale-[1.02] font-medium">
                  <Utensils className="w-4 h-4" /> Mr. Frogs
                </button>
                <button onClick={() => handleChatOption('bye')} className="p-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl text-xs text-gray-700 flex items-center justify-center gap-1 transition-all hover:scale-[1.02] font-medium">
                  👋 ¡Adiós!
                </button>
              </div>
            </div>
          )}

          {/* Frog Avatar */}
          {!showChat && (
            <div 
              className={`w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-xl flex items-center justify-center border-4 border-white ml-auto cursor-pointer hover:scale-110 transition-transform overflow-hidden ${frogMood === 'excited' ? 'animate-bounce' : ''}`}
              onClick={() => handleChatOpen()}
            >
              <FrogCharacter mood={frogMood} isWalking={false} direction="right" isThinking={false} />
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </>
  );
};

export { SmartFrogMascot };
export default SmartFrogMascot;
